/**
 * API Client
 * Core client for making HTTP requests to the backend
 */

import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import localAuthService from '../../services/local-auth.service';
import { apiConfig } from '../../config/api-config';

const API_BASE_URL = apiConfig.baseURL || import.meta.env.VITE_API_URL || 'http://localhost:3002';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Request interceptor to add Cognito auth token
    this.client.interceptors.request.use(
      async (config) => {
        try {
          // Get auth headers from local auth service
          const token = localAuthService.getToken();
          const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
          Object.assign(config.headers, authHeaders);
        } catch (error) {
          // If no auth headers available, continue without them
          console.debug('No auth headers available');
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // Try to refresh the session
          try {
            await cognitoAuthService.refreshSession();
            // Retry the original request
            const config = error.config;
            const authHeaders = await cognitoAuthService.getAuthHeaders();
            Object.assign(config.headers, authHeaders);
            return this.client.request(config);
          } catch (refreshError) {
            // Refresh failed, sign out
            await cognitoAuthService.signOut();
            // Only redirect if not already on login page
            if (!window.location.pathname.includes('/login')) {
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }
}

export const apiClient = new ApiClient();